<template>
    <div class="form-content pl-2 pr-2">                 
        <label>For</label>
        <div v-for="(dateRange) in payload.dateRanges">
            {{ getDateRangeText(dateRange) }}
        </div>

        <div>
            Values left blank will not be updated.
        </div>

        <div class="mt-4 mb-4 d-flex flex-wrap">
            <label>
                Toggle all Selected
            </label>                            

            <v-switch v-model="guideRateToggle">
                <label slot="label">Guide Rate</label>
            </v-switch>

            <v-switch v-model="availabilityOnRequestToggle">
                <label slot="label">Availability On Request</label>
            </v-switch>
        </div>

        <div v-if="payload.rangeHasClosedDates" class="d-flex align-center">
            <div class="mb-2 pl-1 pt-2 pb-2 red rounded">
                <v-icon class="mr-4 amber--text">warning</v-icon><label>Whole Property is Closed from {{ payload.rangeClosedDatesFrom }}</label><v-icon class="ml-4 amber--text">warning</v-icon>
            </div>
        </div>

        <!-- error message -->
        <div v-if="payload.errorMessage" class="d-flex align-center">
            <div class="mb-2 pl-1 pt-2 pb-2 red rounded">
                <v-icon class="mr-4 amber--text">warning</v-icon><label>{{ payload.errorMessage }}</label><v-icon class="ml-4 amber--text">warning</v-icon>
            </div>
        </div>

        <div v-if="payload.selectClosed" class="d-flex align-center">
            <div class="mb-2 pl-1 pt-2 pb-2 red rounded">
                <v-icon class="mr-4 amber--text">warning</v-icon><label>Property is Closed</label><v-icon class="ml-4 amber--text">warning</v-icon>
            </div>
        </div>

        <div v-for="(hotelRoom) in payload.hotelRooms" v-if="payload.selectedHotelRooms.includes(hotelRoom.hotelRoomKey) && !payload.selectClosed">
            <div class="pl-1 pt-1 pb-1 room-name d-flex justify-space-between">
                <label>{{ hotelRoom.name }} ({{ hotelRoom.standardOccupancy }} pax)</label> <span class="text-end pr-2" > (0 - {{ hotelRoom.totalNumRooms }})</span>
            </div>

            <div class="mb-2">
                <div v-if="payload.selectAvailability" class="pl-1 pt-1 pb-0 d-flex flex-wrap">
                    <div :class="['mr-6 d-flex value align-center', getAvailabilityStatusClass(hotelRoom.hotelRoomKey)]">
                        <span>Availability</span>
                    </div> 
                    
                    <v-text-field 
                        hide-details 
                        density="compact" 
                        type="text" 
                        class="mr-2 pt-0 mt-0 value"
                        v-model="availabilityValues[hotelRoom.hotelRoomKey]['quantity']"
                        @input="debouncedCheckAvailabilityClash(hotelRoom.hotelRoomKey, 'quantity')" 
                        @change="updateAvailability(hotelRoom.hotelRoomKey, 'quantity')"
                    />

                    <div style="max-width: 50px;">
                        <v-icon 
                            v-if="availabilityClashes[hotelRoom.hotelRoomKey]['quantity']"
                            class="red--text"
                            :title="availabilityClashes[hotelRoom.hotelRoomKey]['quantity']">
                            warning
                        </v-icon>
                    </div>

                    <v-select 
                        hide-details
                        item-text="text" 
                        item-value="value" 
                        :items="availabilityStatuses"
                        placeholder="Availability Status" 
                        clearable
                        v-model="availabilityValues[hotelRoom.hotelRoomKey]['status_key']" 
                        class="mr-2 mt-0 pt-0 value"
                        @input="debouncedCheckAvailabilityClash(hotelRoom.hotelRoomKey, 'status_key')"
                        @change="updateAvailability(hotelRoom.hotelRoomKey, 'status_key')"
                    />

                    <div style="max-width: 50px;">
                        <v-icon 
                            v-if="availabilityClashes[hotelRoom.hotelRoomKey]['status_key']"
                            class="red--text"
                            :title="availabilityClashes[hotelRoom.hotelRoomKey]['status_key']">
                            warning
                        </v-icon>
                    </div>
                </div>

                <div v-for="(rateType) in payload.rateTypes" v-if="payload.selectedRateTypes.includes(rateType.rateTypeKey) && roomHasRateType(hotelRoom, rateType.rateTypeKey)">
                    <div class="pl-1 pt-1 pb-1 d-flex flex-wrap">
                        <div :class="['white--text mr-6 value d-flex align-center', getRateStatusClass(hotelRoom.hotelRoomKey, rateType.rateTypeKey)]">
                            <span>{{ rateType.name }}</span>
                        </div> 
                        
                        <v-text-field 
                            density="compact" 
                            hide-details 
                            class="mr-2 pt-0 mt-0 value"
                            type="text" 
                            v-model="rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['rate_standard']"
                            @input="debouncedCheckRateClash(rateType.rateTypeKey, hotelRoom.hotelRoomKey, 'rate_standard')"
                            @change="updateStandardRate(hotelRoom.hotelRoomKey, rateType.rateTypeKey, 'rate_standard')"
                            /> 
                        <div style="max-width: 50px;">
                            <v-icon 
                                v-if="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['rate_standard']"
                                class="red--text"
                                :title="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['rate_standard']">
                                warning
                            </v-icon>
                        </div>

                        <v-select 
                            hide-details
                            item-text="text" 
                            item-value="value" 
                            :items="rateStatuses" 
                            class="mr-2 mt-0 pt-0 value"
                            clearable
                            placeholder="Rate Type"
                            v-model="rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['rate_status']" 
                            @input="debouncedCheckRateClash(rateType.rateTypeKey, hotelRoom.hotelRoomKey, 'rate_status')"
                            @change="updateStandardRate(hotelRoom.hotelRoomKey, rateType.rateTypeKey, 'rate_status')"
                        />

                        <div style="max-width: 50px;">
                            <v-icon
                                v-if="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['rate_status']"
                                class="red--text"
                                :title="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['rate_status']">
                                warning
                            </v-icon>
                        </div>

                    </div>

                    <div v-for="(additionalRate) in payload.additionalRates">
                        <div class="mb-1 pl-1 d-flex flex-wrap" v-if="payload.selectedAdditionalRates.includes(additionalRate.field) && !hasUniversalRates && hotelRoom[additionalRate.field]">
                            <div class="grey white--text mr-6 value d-flex align-center">
                                <span>{{ additionalRate.text }}</span>
                            </div> 
                            <v-text-field 
                                density="compact" 
                                hide-details 
                                class="mr-2 pt-0 mt-0 value" 
                                type="text" 
                                v-model="rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey][additionalRate.field]"
                                @input="debouncedCheckRateClash(rateType.rateTypeKey, hotelRoom.hotelRoomKey, additionalRate.field)"
                                @change="updateAdditionalRate(hotelRoom.hotelRoomKey, rateType.rateTypeKey, additionalRate.field)"
                                />

                            <div style="max-width: 50px;">
                                <v-icon 
                                    v-if="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey][additionalRate.field]"
                                    :title="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey][additionalRate.field]" 
                                    class="red--text">
                                    warning
                                </v-icon>
                            </div>
                        </div>
                    </div>


                    <div class="mb-1 pl-1 d-flex flex-wrap" v-if="payload.selectedRestrictions.includes('min_stay')">
                        <div class="grey white--text mr-6 value d-flex align-center">
                            <span>Minimum Stay</span>
                        </div> 
                        <v-text-field 
                            density="compact" 
                            hide-details 
                            class="mr-2 pt-0 mt-0 value" 
                            type="text" 
                            v-model="rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['min_stay']" 
                            @input="debouncedCheckRateClash(rateType.rateTypeKey, hotelRoom.hotelRoomKey, 'min_stay')"
                            @change="updateRestriction(hotelRoom.hotelRoomKey, rateType.rateTypeKey, 'min_stay')"
                            />

                        <div style="max-width: 50px;" v-if="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['min_stay']">
                            <v-icon
                                class="red--text"
                                :title="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['min_stay']"
                                >
                                warning
                            </v-icon>
                        </div>
                    </div>
                    <!-- max_stay -->
                    <div class="mb-1 pl-1 d-flex flex-wrap" v-if="payload.selectedRestrictions.includes('max_stay')">
                        <div class="grey white--text mr-6 value d-flex align-center">
                            <span>Maximum Stay</span>
                        </div> 
                        <v-text-field 
                            density="compact" 
                            hide-details 
                            class="mr-2 pt-0 mt-0 value" 
                            type="text" 
                            v-model="rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['max_stay']" 
                            @input="debouncedCheckRateClash(rateType.rateTypeKey, hotelRoom.hotelRoomKey, 'max_stay')"
                            @change="updateRestriction(hotelRoom.hotelRoomKey, rateType.rateTypeKey, 'max_stay')"
                            />
                        <div style="max-width: 50px;" v-if="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['max_stay']">
                            <v-icon
                                class="red--text"
                                :title="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['max_stay']"
                                >
                                warning
                            </v-icon>
                        </div>
                    </div>

                    <!--  check in -->
                    <div class="mb-1 pl-1 d-flex flex-wrap" v-if="payload.selectedRestrictions.includes('days_checkin') && rateType.is_checkinout_variable">
                        <div class="grey white--text mr-6 value d-flex align-center">
                            <span>Check-In</span>
                        </div> 
                        <v-select 
                            hide-details
                            item-text="text"
                            item-value="value"
                            :items="checkInOutStatuses"
                            class="mr-2 mt-0 pt-0 value"
                            clearable
                            v-model="rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['days_checkin']" 
                            @input="debouncedCheckRateClash(rateType.rateTypeKey, hotelRoom.hotelRoomKey, 'days_checkin')"
                            @change="updateRestriction(hotelRoom.hotelRoomKey, rateType.rateTypeKey, 'days_checkin')"
                            />

                        <div style="max-width: 50px;" v-if="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['days_checkin']">
                            <v-icon
                                class="red--text"
                                :title="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['days_checkin']"
                                >
                                warning
                            </v-icon>
                        </div>
                    </div>

                    <!-- check out -->
                     <div class="mb-1 pl-1 d-flex flex-wrap" v-if="payload.selectedRestrictions.includes('days_checkout') && rateType.is_checkinout_variable">
                        <div class="grey white--text mr-6 value d-flex align-center">
                            <span>Check-Out</span>
                        </div> 
                        
                        <v-select 
                            hide-details
                            item-text="text"
                            item-value="value"
                            :items="checkInOutStatuses"
                            class="mr-2 mt-0 pt-0 value"
                            clearable
                            v-model="rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['days_checkout']" 
                            @input="debouncedCheckRateClash(rateType.rateTypeKey, hotelRoom.hotelRoomKey, 'days_checkout')"
                            @change="updateRestriction(hotelRoom.hotelRoomKey, rateType.rateTypeKey, 'days_checkout')"
                            />

                        <div style="max-width: 50px;" v-if="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['days_checkout']">
                            <v-icon
                                class="red--text"
                                :title="rateClashes[hotelRoom.hotelRoomKey][rateType.rateTypeKey]['days_checkout']"
                                >
                                warning
                            </v-icon>
                        </div>
                    </div>
                </div>
            </div>                            
        </div>

        <!-- universal rates -->
        <div v-if="hasUniversalRates">
            <div class="pl-1 pr-1 pt-1 pb-1 mb-2 darker-grey white--text" style="min-width: 100%;">
                <span>
                    Extra Beds / Guests (for all room types)
                </span>
            </div>

            <div v-for="(universalRate) in universalRateColumns">
                <div class="mb-1 pl-1 d-flex flex-wrap" v-if="universalRate in payload.universalRates && payload.selectedAdditionalRates.includes(getEquivalentRateDateColumn(universalRate))">
                    <div class="grey white--text mr-6 value d-flex align-center">
                        <span>{{ getUniversalRateText(universalRate) }}</span>
                    </div> 
                    <v-text-field 
                        density="compact" 
                        hide-details 
                        class="mr-2 pt-0 mt-0 value" 
                        type="text" 
                        v-model="universalRateValues[universalRate]"
                        :placeholder="getUniversalRatePlaceholder(payload.universalRates[universalRate])"
                        @change="updateUniversalRate(universalRate)"/>
                </div>
            </div>
        </div>
                
    </div>
</template>

<script>
/**
* A Vue component for the user to select batch loader update values for the fields they have specified
*
*/

import { debounce } from 'lodash';

export default {
    name: 'hotel-batch-loader-value-selector',
    props: [
        'payload',
        ],
    data () {
        return {
             
            universalRateColumns: [
                'extra_adult_rate_amount',
                'extra_baby_rate_amount',
                'extra_child_rate_amount',
                'extra_oldchild_rate_amount'
            ],

            availabilityStatuses: [
                {text: 'Available', value: 41},
                {text: 'On Request', value: 42},
                {text: 'Not Available', value: 43},

            ],

            rateStatuses: [
                {text: 'Exact', value: 'exact'},
                {text: 'Guide', value: 'indicative'},
                {text: 'Closed', value: 'closed'},
            ],

            checkInOutStatuses: [
                {text: 'No Change', value: null},
                {text: 'Allowed', value: 0},
                {text: 'Not Allowed', value: 1},
            ],

            availabilityValues: {},
            rateValues: {},
            universalRateValues: {},

            availabilityOnRequestToggle: true,
            guideRateToggle: false,

            rateClashes: {},
            availabilityClashes: {}
        }
    },

    methods: {
        getDateRangeText(dateRange){
            let from = new Date(dateRange.from.year, dateRange.from.month - 1, dateRange.from.day);
            let to = new Date(dateRange.to.year, dateRange.to.month - 1, dateRange.to.day);

            return from.toDateString() + ' - ' + to.toDateString();
        },

        initData(){
            this.initValues();
            this.initRateClashes();
            this.initAvailabilityClashes();
        },

        initValues(){
            if (this.payload.hotelRooms){
                let availabilityValues = {};
                let rateValues = {};

                this.payload.hotelRooms.forEach((hotelRoom) => {
                    availabilityValues[hotelRoom.hotelRoomKey] = {
                        'status_key': null,
                        'quantity': null
                    };
                    rateValues[hotelRoom.hotelRoomKey] = {};
               
                    if (this.payload.rateTypes){
                        this.payload.rateTypes.forEach((rateType) => {
                            rateValues[hotelRoom.hotelRoomKey][rateType.rateTypeKey] = {};
                        });
                    }
                });

                this.$set(this, 'availabilityValues', availabilityValues);
                this.$set(this, 'rateValues', rateValues);
            }
        },

        checkRateClash(rateType, hotelRoom, updateColumn){
            if (this.rateValues[hotelRoom][rateType][updateColumn] == null || this.rateValues[hotelRoom][rateType][updateColumn] == ''){
                this.rateClashes[hotelRoom][rateType][updateColumn] = null
                return;
            }

            let data = this.buildRateClashPostData(rateType, hotelRoom, updateColumn);

            axios.get('/api/hotel-batch-loader/check-rate-clash', {'params': data})
                .then((response) => {
                    if (response.data.clash == true){
                        this.rateClashes[hotelRoom][rateType][updateColumn] = response.data.message;
                    } else {
                        this.rateClashes[hotelRoom][rateType][updateColumn] = null;
                    }
            });
        },

        buildRateClashPostData(rateType, hotelRoom, updateColumn){
            let newValue = this.rateValues[hotelRoom][rateType][updateColumn];

            if (updateColumn == 'rate_status'){
                updateColumn = 'is_indicative';
                newValue = this.rateValues[hotelRoom][rateType]['rate_status'] == 'indicative' ? 1 : 0;
            }
            return {
                'rateType': rateType,
                'hotelRoom': hotelRoom,
                'updateColumn': updateColumn,
                'newValue': newValue,
                'dateRanges': this.payload.dateRanges,
                'days': this.payload.selectedDays
            }
        },

        checkAvailabilityClash(hotelRoom, updateColumn){
            if (this.availabilityValues[hotelRoom][updateColumn] == null || this.availabilityValues[hotelRoom][updateColumn] == ''){
                this.availabilityClashes[hotelRoom][updateColumn] = null
                return;
            }

            let data = this.buildAvailabilityClashPostData(hotelRoom, updateColumn);

            axios.get('/api/hotel-batch-loader/check-availability-clash', {'params': data})
                .then((response) => {
                    if (response.data.clash == true){
                        this.availabilityClashes[hotelRoom][updateColumn] = response.data.message;
                    } else {
                        this.availabilityClashes[hotelRoom][updateColumn] = null;
                    }
            });
        },

        buildAvailabilityClashPostData(hotelRoom, updateColumn){
            return {
                'hotelRoom': hotelRoom,
                'updateColumn': updateColumn,
                'newValue': this.availabilityValues[hotelRoom][updateColumn],
                'dateRanges': this.payload.dateRanges,
                'days': this.payload.selectedDays
            }
        },

        getAvailabilityStatusClass(hotelRoomKey){
            if (this.availabilityValues[hotelRoomKey].quantity == 0){
                return 'room-unavailable white--text';
            }

            if (this.availabilityValues[hotelRoomKey].status_key == 41 && this.availabilityValues[hotelRoomKey]){
                return 'room-available white--text';
            }

            if (this.availabilityValues[hotelRoomKey].status_key == 42 && this.availabilityValues[hotelRoomKey]){
                return 'room-on-request';
            }

            return 'darker-grey white--text ';
        },

        getRateStatusClass(hotelRoomKey, rateTypeKey){
            if (!this.rateValues[hotelRoomKey][rateTypeKey]['rate_standard']){
                return 'darker-grey';
            }

            if (this.rateValues[hotelRoomKey][rateTypeKey]['rate_status'] != 'indicative'){
                return 'darker-grey';
            }
            
            return 'font-italic purple';
        },

        getEquivalentRateDateColumn(rateDateColumn){
            if (rateDateColumn == 'extra_baby_rate_amount'){
                return 'rate_extra_baby';
            }

            if (rateDateColumn == 'extra_child_rate_amount'){
                return 'rate_extra_child';
            }

            if (rateDateColumn == 'extra_oldchild_rate_amount'){
                return 'rate_extra_oldchild';
            }

            if (rateDateColumn == 'extra_adult_rate_amount'){
                return 'rate_extra_adult';
            }
        },

        getDefaultRateText(rateDateColumn){
            if (rateDateColumn == 'extra_baby_rate_amount'){
                return 'Extra Cot Price';
            }

            if (rateDateColumn == 'extra_child_rate_amount'){
                return 'Extra Child Bed Price';
            }

            if (rateDateColumn == 'extra_oldchild_rate_amount'){
                return 'Extra Old Child Bed Price';
            }

            if (rateDateColumn == 'extra_adult_rate_amount'){
                return 'Extra Adult Price';
            }
        },

        getUniversalRateText(field){
            let rateCol = this.getEquivalentRateDateColumn(field);

            if (!rateCol){
                return '';
            }

            let text = this.payload.additionalRates.find(rateOption => rateOption.field == rateCol)?.text ?? this.getDefaultRateText(rateCol);
            
            return text;
        },

        getUniversalRatePlaceholder(value){
            if (!value){
                return 'free';
            }

            return value;
        },

        getIndicativeRateText(isExactRate){
            if (isExactRate){
                return 'Exact Rate';
            }

            return 'Guide Rate';
        },

        roomHasRateType(hotelRoom, rateTypeKey){
            return hotelRoom.rateTypes.includes(rateTypeKey);
        },

        updateAvailability(hotelRoomKey, column){
            let data = {
                'hotelRoomKey': hotelRoomKey,
                'column': column,
                'value': this.availabilityValues[hotelRoomKey][column]
            }

            this.$emit('update-availability', data);

            if (column == 'quantity' && this.availabilityValues[hotelRoomKey][column] == 0){
                this.availabilityValues[hotelRoomKey]['status_key'] = 43;
                this.updateAvailability(hotelRoomKey, 'status_key');
            }
        },

        updateStandardRate(hotelRoomKey, rateTypeKey, column){
            let data = {
                'hotelRoomKey': hotelRoomKey,
                'rateTypeKey': rateTypeKey,
                'column': column,
                'value': this.rateValues[hotelRoomKey][rateTypeKey][column]
            }

            this.$emit('update-standard-rate', data);
        },

        updateAdditionalRate(hotelRoomKey, rateTypeKey, column){
            let data = {
                'hotelRoomKey': hotelRoomKey,
                'rateTypeKey': rateTypeKey,
                'column': column,
                'value': this.rateValues[hotelRoomKey][rateTypeKey][column]
            }

            this.$emit('update-additional-rate', data);
        },

        updateRestriction(hotelRoomKey, rateTypeKey, column){
            let data = {
                'hotelRoomKey': hotelRoomKey,
                'rateTypeKey': rateTypeKey,
                'column': column,
                'value': this.rateValues[hotelRoomKey][rateTypeKey][column]
            }

            this.$emit('update-restriction', data);
        },

        updateUniversalRate(column){
            let data = {
                'column': column,
                'value': this.universalRateValues[column]
            }

            this.$emit('update-universal-rate', data);
        },  

        initRateClashes(){
            const rateClashes = {};

            this.payload.hotelRooms.forEach(hotelRoom => {
                const rateTypeMap = {};

                this.payload.rateTypes.forEach(rateType => {
                    rateTypeMap[rateType.rateTypeKey] = {
                        rate_standard: null,
                        rate_status: null,
                        rate_extra_adult: null,
                        rate_extra_baby: null,
                        rate_extra_child: null,
                        rate_extra_oldchild: null,
                        min_stay: null,
                        max_stay: null,
                        days_checkin: null,
                        days_checkout: null
                    };
                });

                rateClashes[hotelRoom.hotelRoomKey] = rateTypeMap;
            });

            this.$set(this, 'rateClashes', rateClashes);
        },

        initAvailabilityClashes(){
            const availabilityClashes = {};

            this.payload.hotelRooms.forEach(hotelRoom => {
                availabilityClashes[hotelRoom.hotelRoomKey] = {
                    'status_key': null,
                    'quantity': null
                }
            });

            this.$set(this, 'availabilityClashes', availabilityClashes);
        },
    },

    /**
     * Once mounted this component needs to load all available models
     */
    mounted() {
        this.initData();
    },

    created() {
        this.debouncedCheckAvailabilityClash = debounce(this.checkAvailabilityClash, 500);
        this.debouncedCheckRateClash = debounce(this.checkRateClash, 500);
    },

    computed: {
        hasUniversalRates() {
            return this.payload.universalRates != [] && Object.keys(this.payload.universalRates).length > 0;
        },

    },

    watch: {
        availabilityOnRequestToggle: function() {
            for (let [hotelRoomKey, availability] of Object.entries(this.availabilityValues)){

                if (this.payload.selectedHotelRooms.includes(Number(hotelRoomKey))){
                    availability.status_key = this.availabilityOnRequestToggle ? 42 : 41;
                    this.updateAvailability(hotelRoomKey, 'status_key');

                    this.checkAvailabilityClash(hotelRoomKey, 'status_key');
                }
            }
        },

        guideRateToggle: function() {
            for (let [roomKey, rates] of Object.entries(this.rateValues)){
                for (let [rateKey, rate] of Object.entries(rates)){
                    if (this.payload.selectedRateTypes.includes(Number(rateKey))){
                        rate.rate_status = this.guideRateToggle ? 'indicative' : 'exact';
                        this.updateStandardRate(roomKey, rateKey, 'rate_status');
                        this.checkRateClash(rateKey, roomKey, 'rate_status');                        
                    }
                }
            }
        },

        payload: function() {
            if (this.payload.resetValues){
                this.initData();
                this.$emit('values-reset');
            }
        }
    }
}
</script>

<style>
    
</style>
